import { type ReactNode, useEffect, useState } from "react";
import { InformeImprimible } from "./InformeImprimible.js";
import { GenerarInforme, IdeasDeKeywords, MapaDeSecciones, TrabajoDelCliente } from "./SeoAFondo.js";
import { Busquedas, Competencia, Cpc, LoQueSeBusca, SelectorDeZona, useGoogleAds } from "./KeywordsDeGoogle";
import { Contenidos as CalendarioDeContenidos } from "./Contenidos";
import type { Donde } from "./ir-al-cliente.js";
import {
  CabeceraSeo,
  mesLegible,
  PanelSeo,
  useSeoDelMes,
  type VistaDelCliente,
} from "./PanelSeo.js";

/**
 * El SEO de un cliente, organizado por decisiones y no por tipos de dato.
 *
 * La primera versión tenía una caja por cada cosa que se guarda: keywords en
 * una lista, si eran principales en otra, las posiciones de Google en una
 * tabla y las de las IAs en otra. Jorge dijo que seguía todo mezclado, y tenía
 * razón por un motivo concreto: **una keyword estaba partida en cuatro
 * sitios**. Para saber cómo iba «agencia seo bilbao» había que mirar en cuatro
 * y juntarlo de cabeza.
 *
 * Ahora hay UNA FILA POR KEYWORD con todo lo suyo, y las cajas responden a las
 * preguntas que se hace un director de SEO al abrir un cliente:
 *
 *   1. ¿Voy bien? — arriba, en una línea con el número que lo prueba.
 *   2. ¿Qué decidimos? — la estrategia.
 *   3. ¿Qué mando hacer? — el trabajo del mes, ordenado por impacto.
 *   4. ¿Cómo va cada cosa? — la tabla de keywords.
 *   5. ¿Qué hay que escribir? — los contenidos.
 *   6. ¿Qué pasó antes? — los informes congelados.
 *
 * Nada de esto lo lee el director: él consulta la base de datos con sus
 * herramientas. Esta pantalla es para Jorge, y por eso se ordena como se toman
 * las decisiones y no como están guardados los datos.
 */

export interface KeywordObjetivo {
  texto: string;
  tipo: "servicio" | "informativa";
  principal: boolean;
  pagina: string | null;
  posicionInicial: number | null;
  desde: string | null;
  /**
   * ¿Venía en el último informe de SE Ranking?
   *
   * `undefined` en las guardadas antes de existir el campo: se rellena en
   * cuanto entre el siguiente informe. `false` es una que ya no se mide.
   */
  enSeRanking?: boolean;
}

export interface Contenido {
  titulo: string;
  keyword?: string;
  mes?: string;
  estado?: "previsto" | "escrito" | "publicado";
  /** El encargo para quien lo escriba. */
  notas?: string;
  /**
   * Artículo de blog, o página de servicio.
   *
   * No es lo mismo y no se escribe igual: uno atrae a quien todavía está
   * preguntando y la otra tiene que vender. Mezclarlos en una lista hace que
   * se encarguen con el mismo brief.
   */
  tipo?: "blog" | "servicio";
  /**
   * De dónde salió la idea.
   *
   * Importa al revisar: una idea detectada en Search Console tiene un número
   * detrás y una escrita a mano tiene un criterio. Las dos valen, pero no se
   * defienden igual delante de un cliente.
   */
  origen?: "detectado" | "bilbobot" | "mano";
  /** Qué página de servicio viene a alimentar este artículo. */
  apoyaA?: string;
  /** Por qué merece la pena escribirlo, con el número si lo hay. */
  porQue?: string;
}

export interface FichaSeo {
  estrategia: string | null;
  estrategiaDesde: string | null;
  estrategiasAnteriores: {
    texto: string;
    desde: string;
    hasta: string | null;
  }[];
  deServicio: KeywordObjetivo[];
  informativas: KeywordObjetivo[];
  planDeContenidos: Contenido[];
}

/** Dónde está hoy una keyword, en Google y en las IAs. */
interface Posicion {
  enGoogle: { mes: string; posicion: number | null } | null;
  antes: { mes: string; posicion: number | null } | null;
  enIa: {
    mes: string;
    modelo?: string;
    mencionado: boolean;
    posicion: number | null;
    deCuantos: number;
  } | null;
}

interface EnLasIas {
  keyword: string;
  teNombran: boolean;
  puesto: number | null;
  deCuantos: number;
  salenEnTuLugar: {
    nombre: string;
    web?: string | null;
    porQue?: string | null;
  }[];
}

interface ResumenKw {
  cuantas: number;
  medidas: number;
  enPrimeraPagina: number;
  mejoran: number;
  empeoran: number;
}

interface Accion {
  queHacer: string;
  porQue: string;
  como: string | null;
  cuidado: string | null;
  seNotaEn: string | null;
  clicsEnJuego: number;
}

/**
 * Lo que trae dentro un informe congelado.
 *
 * Todo opcional a propósito: un informe viejo se guardó con lo que se sabía
 * hacer entonces, y tiene que seguir enseñándose aunque los nuevos lleven más
 * cosas. Es el precio de congelarlos, y es el precio correcto.
 */
interface CuerpoInforme {
  paraElCliente?: {
    servicio?: ResumenKw;
    informativas?: ResumenKw;
    busqueda?: {
      clics: number;
      clicsAntes: number;
      impresiones: number;
      impresionesAntes: number;
      posicionMedia: number;
      posicionMediaAntes: number;
    } | null;
    trafico?: { sesiones: number; sesionesAntes: number } | null;
    /** Cuánta gente busca lo que trabajamos, de Google Ads (25/10/2026). */
    demanda?: {
      zona: string;
      total: number;
      keywords: { keyword: string; busquedas: number; posicion: number | null; principal: boolean }[];
    } | null;
    contenidosPublicados?: { canal: string; titulo: string }[];
    seHizo?: string[];
    siFunciono?: string[];
    problemasArreglados?: string[];
    enLasIas?: EnLasIas[] | string;
  };
  interno?: {
    planDelMes?: {
      propuestas: number;
      hechas: number;
      descartadas: number;
      sinHacer: string[];
    };
    problemasNuevos?: string[];
  };
  planDelMesQueViene?: Accion[];
  tecnicoPorArreglar?: {
    problema: string;
    paginas: number;
    como: string;
    cuidado?: string | null;
    seNotaEn?: string | null;
  }[];
}

interface Informe {
  mes: string;
  generadoEl: string;
  comentario: string | null;
  /** Lo que escribe Jorge, aparte de los números. Ver `redaccion` en la base. */
  redaccion?: {
    lectura?: string;
    estrategia?: string;
    plan?: string;
  };
  cuerpo: CuerpoInforme;
}

/**
 * El día en que se dejó de pedir «los últimos 28 días» y se pasó al mes exacto.
 *
 * Todo informe generado antes de esta fecha mide un rango que se solapa con el
 * mes anterior, aunque su título diga otra cosa. Se compara contra
 * `generadoEl`, que se guarda como texto ISO.
 */
const FIN_DE_LOS_28_DIAS = "2026-08-25";

/**
 * «1 búsqueda» / «6 búsquedas», con el número en negrita.
 *
 * Las frases de un informe las lee un cliente, y «de las 1 búsquedas que
 * trabajamos» resta credibilidad a todo lo que venga detrás.
 */
function enPlural(n: number, una: string, varias: string): ReactNode {
  return (
    <>
      {n === 1 ? "la " : "las "}
      <b>{n}</b> {n === 1 ? una : varias}
    </>
  );
}

/** Una cifra con su anterior al lado. Sin comparación, un número no dice nada. */
function Cifra({
  que,
  ahora,
  antes,
  menosEsMejor,
}: {
  que: string;
  ahora: number;
  antes: number;
  menosEsMejor?: boolean;
}) {
  const sube = ahora > antes;
  const bien = menosEsMejor ? !sube : sube;
  return (
    <div className="cifra">
      <span className="que">{que}</span>
      <strong>{ahora.toLocaleString("es-ES")}</strong>
      {antes !== ahora && (
        <span className={bien ? "mejor" : "peor"}>
          {sube ? "▲" : "▼"} {Math.abs(ahora - antes).toLocaleString("es-ES")}
        </span>
      )}
    </div>
  );
}

/**
 * Los apartados que se TOCAN, dentro de la pantalla «El cliente».
 *
 * Antes eran siete, en una fila plana debajo de las seis del panel: trece
 * pestañas al mismo nivel sin nada que dijera cuál se mira cada día y cuál se
 * toca una vez al trimestre.
 *
 * Los otros dos —informes y estrategia— se han ido a la pantalla «Informes»:
 * la estrategia no es gestión, es el marco de lo que se le cuenta al cliente,
 * y se lee al lado del informe y no en un cajón aparte.
 */
const DEL_CLIENTE = [
  ["keywords", "Keywords", "Las búsquedas que se trabajan"],
  ["publicaciones", "Publicaciones", "El calendario de sus artículos, el plan y las ideas"],
  ["tareas", "Recuerdos", "Lo que se sabe de este cliente"],
  ["cliente", "Ficha", "Quién es y qué tiene contratado"],
  ["configuracion", "Conexiones", "Analytics, Search Console y los accesos"],
] as const;

type DelCliente = (typeof DEL_CLIENTE)[number][0];
type Apartado = DelCliente | "informes" | "estrategia";

/**
 * Las tres carpetas de SE Ranking, con su icono.
 *
 * No son tres etiquetas de esta pantalla: son las carpetas en las que Jorge
 * coloca cada keyword allí, y de ahí salen `principal` y `tipo` al importar el
 * informe. Aquí sólo se enseñan.
 *
 * Tres iconos y no una estrella encendida o apagada porque «apagada» decía dos
 * cosas a la vez —secundaria y de blog— y son trabajos distintos: una
 * secundaria se ataca mejorando la página que ya existe, una de blog hay que
 * escribirla.
 */
const CARPETAS = [
  {
    clave: "primaria",
    icono: "★",
    nombre: "PRIMARIAS",
    ayuda: "En PRIMARIAS: es de las que mandan.",
  },
  {
    clave: "secundaria",
    icono: "◆",
    nombre: "SECUNDARIAS",
    ayuda: "En SECUNDARIAS: también trae clientes, y apoya a las primarias.",
  },
  {
    clave: "blog",
    icono: "✎",
    nombre: "BLOG",
    ayuda: "En BLOG: se ataca escribiendo, y alimenta a las de servicio.",
  },
  {
    clave: "fuera",
    icono: "○",
    nombre: "no está en SE Ranking",
    ayuda:
      "No venía en el último informe: no se está midiendo. Suele ser una keyword escrita a mano hace tiempo. Si la quieres, métela en una carpeta de SE Ranking; si no, bórrala.",
  },
] as const;

/**
 * En qué carpeta está una keyword, según lo que trajo el último informe.
 *
 * «Fuera» va primero: si no se está midiendo, saber que es de servicio o de
 * blog da igual — lo que hay que decidir es si sigue o se va.
 */
function carpetaDe(k: KeywordObjetivo): (typeof CARPETAS)[number] {
  if (k.enSeRanking === false) return CARPETAS[3];
  if (k.tipo === "informativa") return CARPETAS[2];
  return k.principal ? CARPETAS[0] : CARPETAS[1];
}

/** De qué IA es la medida de la columna «En IA». */
function queIa(modelo: string | undefined): string {
  if (!modelo) return "Medido en una IA con búsqueda web";
  if (modelo.startsWith("chatgpt")) return "ChatGPT (por Codex, con búsqueda web), medido cada semana";
  if (modelo.startsWith("claude")) return "Claude, con búsqueda web, medido cada mes";
  return `${modelo} por API, con búsqueda web, medido cada mes`;
}

/** El texto tal cual, con las direcciones convertidas en enlaces que se pueden pulsar. */
function conEnlaces(texto: string): ReactNode[] {
  return texto.split(/(https?:\/\/[^\s)]+)/g).map((trozo, i) =>
    /^https?:\/\//.test(trozo) ? (
      <a key={i} href={trozo} target="_blank" rel="noreferrer">
        {trozo}
      </a>
    ) : (
      trozo
    ),
  );
}

export function SeoDelCliente({
  abrirEn,
  clienteId,
  nombreCliente,
  ficha,
  quienEs,
  comoEstaConectado,
  tareas,
}: {
  /** Dónde abrirse, si se llega desde «Mando». */
  abrirEn?: Donde | undefined;
  clienteId: string;
  /** El nombre, para la portada del informe impreso. */
  nombreCliente: string;
  ficha: FichaSeo;
  /** Quien es el cliente: la web, los otros dominios, lo que tiene contratado. */
  quienEs: ReactNode;
  /** Como esta conectado: SE Ranking, Search Console, Analytics y los accesos. */
  comoEstaConectado: ReactNode;
  /** Lo que sabe de este cliente y lo que hay que revisar. */
  tareas: ReactNode;
}) {
  /**
   * La ficha guardada se queda aquí, no se le devuelve al padre.
   *
   * El padre sólo pinta un cliente y no es dueño de la lista, así que pedirle
   * que se actualice obligaría a subir el estado tres niveles para enseñar un
   * dato que ya está aquí.
   */
  const [actual, setActual] = useState(ficha);
  const [estrategia, setEstrategia] = useState(ficha.estrategia ?? "");
  const [editandoEstrategia, setEditandoEstrategia] = useState(false);
  const [keywords, setKeywords] = useState<KeywordObjetivo[]>([]);
  const [contenidos, setContenidos] = useState<Contenido[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [guardado, setGuardado] = useState<string | null>(null);
  const [informes, setInformes] = useState<Informe[]>([]);
  const [posiciones, setPosiciones] = useState<Record<string, Posicion>>({});
  const [abierto, setAbierto] = useState<string | null>(null);
  /* Los datos reales de Google Ads de sus keywords, en su zona (24/10/2026). */
  const google = useGoogleAds(clienteId, ficha.deServicio.length + ficha.informativas.length);

  useEffect(() => {
    setActual(ficha);
    setEstrategia(ficha.estrategia ?? "");
    setKeywords([...ficha.deServicio, ...ficha.informativas]);
    setContenidos(ficha.planDeContenidos ?? []);
    setGuardado(null);
    setAbierto(null);
    // Al cambiar de cliente se recarga todo: dejar en pantalla lo del anterior
    // mientras se lee lo del nuevo es la forma más fácil de guardarle a uno lo
    // que era del otro.
    void fetch(`/api/memoria/clientes/${clienteId}/informes`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : { informes: [] }))
      .then((c: { informes: Informe[] }) => setInformes(c.informes ?? []))
      .catch(() => setInformes([]));

    void fetch(`/api/memoria/clientes/${clienteId}/posiciones`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : { posiciones: {} }))
      .then((c: { posiciones: Record<string, Posicion> }) =>
        setPosiciones(c.posiciones ?? {}),
      )
      .catch(() => setPosiciones({}));
  }, [clienteId]);

  /**
   * Vuelve a leer los informes después de escribir en uno.
   *
   * Hace falta porque el cuerpo del informe se desmonta al plegarlo y se
   * reconstruye desde la lista al abrirlo otra vez: sin recargarla, lo que
   * acabas de guardar desaparece de la pantalla en cuanto pliegas, y parece
   * que no se guardó.
   */
  function recargarInformes(): void {
    void fetch(`/api/memoria/clientes/${clienteId}/informes`, {
      credentials: "same-origin",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((c: { informes: Informe[] } | null) => c && setInformes(c.informes))
      .catch(() => {});
  }

  function cambiar(i: number, campo: Partial<KeywordObjetivo>): void {
    setKeywords((previas) =>
      previas.map((k, n) => (n === i ? { ...k, ...campo } : k)),
    );
    setGuardado(null);
  }

  /** Devuelve si se guardó, para que la estrategia sólo deje de editarse cuando quedó guardada. */
  async function guardar(): Promise<boolean> {
    setOcupado(true);
    try {
      const r = await fetch(`/api/memoria/clientes/${clienteId}/seo`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          estrategia,
          // Las vacías no se guardan: una fila a medio escribir no es una
          // keyword, y guardarla la deja como objetivo sin que nadie lo quiera.
          keywords: keywords
            .filter((k) => k.texto.trim())
            /*
             * `principal` NO se manda: lo decide la carpeta de SE Ranking, y
             * el servidor conserva el suyo pase lo que pase. Mandarlo desde
             * aquí sería dejar abierta la puerta que se acaba de cerrar.
             */
            .map((k) => ({
              texto: k.texto.trim(),
              tipo: k.tipo,
              pagina: k.pagina?.trim() || null,
            })),
          contenidos: contenidos.filter((c) => c.titulo.trim()),
        }),
      });
      if (!r.ok) {
        setGuardado("No se ha podido guardar.");
        return false;
      }
      const { ficha: nueva } = (await r.json()) as { ficha: FichaSeo };
      setActual(nueva);
      setKeywords([...nueva.deServicio, ...nueva.informativas]);
      setContenidos(nueva.planDeContenidos ?? []);
      setGuardado("Guardado.");
      return true;
    } catch {
      setGuardado("No se ha podido guardar.");
      return false;
    } finally {
      setOcupado(false);
    }
  }

  const ultimo = informes[0];
  const de = (k: KeywordObjetivo) => posiciones[k.texto.trim().toLowerCase()];

  /**
   * El orden de la tabla: primero las que pagan.
   *
   * Principales de servicio, resto de servicio, y las de blog al final. Es el
   * orden en que se mira cuando alguien pregunta cómo va un cliente, y el que
   * evita que cuarenta keywords de blog tapen a las cinco que importan.
   *
   * Ordena sólo por esas dos cosas, así que es estable: una lista que se
   * recoloca sola mientras escribes hace perder la fila que estabas tocando.
   */
  const orden = keywords
    .map((k, i) => ({ k, i }))
    .sort(
      (a, b) =>
        Number(a.k.tipo === "informativa") -
          Number(b.k.tipo === "informativa") ||
        Number(b.k.principal) - Number(a.k.principal),
    );

  /** Cuál de las tres pantallas se está viendo. */
  const [vista, setVista] = useState<VistaDelCliente>(
    abrirEn === "contenidos" || abrirEn === "keywords" ? "cliente" : abrirEn ?? "como-va",
  );
  /** Y dentro de «El cliente», cuál de sus cinco apartados. */
  const [apartado, setApartado] = useState<DelCliente>(abrirEn === "contenidos" ? "publicaciones" : "keywords");

  /* Los informes van debajo de la estrategia: si se pidieron ellos, se baja hasta ahí. */
  useEffect(() => {
    if (abrirEn !== "informes") return;
    /* Lo de arriba carga después y empuja: se vuelve a bajar mientras carga. */
    const ts = [400, 1200, 2500, 4000].map((ms) =>
      setTimeout(() => document.getElementById("informes-del-cliente")?.scrollIntoView({ block: "start" }), ms),
    );
    return () => ts.forEach(clearTimeout);
  }, [abrirEn]);

  /*
   * El SEO del mes, cargado UNA VEZ para las tres pantallas.
   *
   * Vive aquí y no dentro del panel de métricas porque el selector de mes de
   * la cabecera manda sobre las tres: si cada pantalla cargara lo suyo, elegir
   * marzo en una y saltar a otra enseñaría dos meses distintos con el mismo
   * selector arriba diciendo uno solo.
   */
  const seo = useSeoDelMes(clienteId);

  /**
   * Los siete apartados de gestión, ya construidos.
   *
   * Se arman aquí y no en cada sitio donde se pintan porque su estado —lo
   * escrito y todavía sin guardar— es de este componente: sacarlos a
   * componentes sueltos partiría el formulario en dos mitades que tendrían que
   * seguir compartiendo estado.
   *
   * Antes esto se rellenaba DENTRO del prop de `PanelSeo`, con una asignación
   * en medio del JSX, y se leía cincuenta líneas más abajo. Funcionaba sólo
   * porque los hijos de un elemento se evalúan en orden: mover el `<nav>` por
   * encima del panel no habría dado error de compilación, habría pintado
   * `null` y dejado la pantalla muda.
   */
  const gestion: Record<Apartado, ReactNode> = {
    keywords: (
      <>
        {/**
         * UNA FILA POR KEYWORD, con todo lo suyo.
         *
         * Es el cambio que arregla lo de «está todo mezclado»: antes el texto y
         * la URL estaban en una lista, la marca de principal en otra, la
         * posición de Google en una tabla y la de la IA en otra. La keyword es
         * la unidad con la que se piensa; partirla en cuatro obliga a
         * recomponerla de cabeza cada vez.
         */}
        <section className="caja-seo">
          <header>
            <h3>Keywords</h3>
            <span className="pista">
              El icono dice de qué carpeta de SE Ranking viene cada una:{" "}
              {CARPETAS.map((c, n) => (
                <span key={c.clave}>
                  {n > 0 && " · "}
                  <span className={`marca-kw ${c.clave}`}>{c.icono}</span>{" "}
                  {c.nombre}
                </span>
              ))}
              . Para cambiarlo, muévela de carpeta allí.
            </span>
            <SelectorDeZona datos={google} />
          </header>

          <div className="tabla-kw">
            <div className="cabecera-kw">
              <span>Búsqueda</span>
              <span>Página que la ataca</span>
              <span>En Google</span>
              <span>En IA</span>
              <span title={`Búsquedas al mes en ${google.zona}, según Google Ads`}>Al mes</span>
              <span>Competencia</span>
              <span>Coste/clic</span>
              <span />
            </div>

            {orden.map(({ k, i }) => {
              const p = de(k);
              const hoy = p?.enGoogle?.posicion ?? null;
              const antes = p?.antes?.posicion ?? null;
              // Menos es mejor: de la 30 a la 12 son dieciocho puestos GANADOS.
              const gana =
                hoy !== null && antes !== null ? antes - hoy : null;
              const ia = p?.enIa;

              return (
                <div
                  className={
                    k.principal ? "fila-kw principal" : "fila-kw"
                  }
                  key={i}
                >
                  <span className="celda texto">
                    {/*
                     * El icono se MIRA, no se pulsa.
                     *
                     * La estrella era un botón y se cambiaba sin querer al
                     * pinchar cerca del texto de la keyword. Y lo que cambiaba
                     * no era una preferencia de esta pantalla: dice en qué
                     * carpeta de SE Ranking está, y eso se decide allí. Dos
                     * sitios decidiendo lo mismo es un sitio de más, y ganaba el
                     * último que guardara.
                     */}
                    <span
                      className={`marca-kw ${carpetaDe(k).clave}`}
                      title={`${carpetaDe(k).ayuda} Para cambiarlo, muévela de carpeta en SE Ranking.`}
                      aria-label={carpetaDe(k).nombre}
                    >
                      {carpetaDe(k).icono}
                    </span>
                    <input
                      value={k.texto}
                      onChange={(e) =>
                        cambiar(i, { texto: e.target.value })
                      }
                      placeholder="la búsqueda"
                    />
                  </span>

                  <span className="celda">
                    <input
                      value={k.pagina ?? ""}
                      onChange={(e) =>
                        cambiar(i, { pagina: e.target.value })
                      }
                      placeholder={
                        k.tipo === "servicio"
                          ? "página destino"
                          : "vacío = hay que escribirla"
                      }
                    />
                  </span>


                  <span className="celda dato">
                    {hoy !== null ? (
                      <>
                        <strong>{hoy}</strong>
                        {gana !== null && gana !== 0 && (
                          <span className={gana > 0 ? "mejor" : "peor"}>
                            {gana > 0 ? "▲" : "▼"} {Math.abs(gana)}
                          </span>
                        )}
                        {/* De dónde partió. No se resta con lo de hoy, que viene
                    de otra fuente: va como referencia, no como cuenta. */}
                        {k.posicionInicial !== null && (
                          <span
                            className="salida"
                            title="Posición el día que se decidió atacarla, según Search Console"
                          >
                            salió {k.posicionInicial}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="sin">sin medir</span>
                    )}
                  </span>

                  <span className="celda dato">
                    {ia ? (
                      ia.mencionado ? (
                        <strong className="mejor" title={queIa(ia.modelo)}>
                          {ia.posicion}º
                          <span className="sin">
                            {" "}
                            de {ia.deCuantos}
                          </span>
                        </strong>
                      ) : (
                        <span className="peor" title={queIa(ia.modelo)}>no sale</span>
                      )
                    ) : (
                      <span className="sin">
                        {k.principal && k.tipo === "servicio"
                          ? "sin medir"
                          : "—"}
                      </span>
                    )}
                  </span>

                  <span className="celda dato">
                    <Busquedas m={google.porKeyword[k.texto.trim().toLowerCase()]} cargando={google.cargando} />
                  </span>
                  <span className="celda dato">
                    <Competencia m={google.porKeyword[k.texto.trim().toLowerCase()]} />
                  </span>
                  <span className="celda dato">
                    <Cpc m={google.porKeyword[k.texto.trim().toLowerCase()]} />
                  </span>

                  <span className="celda">
                    <button
                      type="button"
                      className="quitar"
                      onClick={() => {
                        setKeywords((previas) =>
                          previas.filter((_, n) => n !== i),
                        );
                        setGuardado(null);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="anadir"
            onClick={() =>
              setKeywords((previas) => [
                ...previas,
                {
                  texto: "",
                  tipo: "servicio",
                  principal: false,
                  pagina: null,
                  posicionInicial: null,
                  desde: null,
                },
              ])
            }
          >
            + añadir keyword
          </button>
        </section>
        <div className="acciones-seo">
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={ocupado}
          >
            {ocupado ? "Guardando…" : "Guardar"}
          </button>
          {guardado && <span className="pista">{guardado}</span>}
        </div>
      </>
    ),
    estrategia: (
      <>
        <ComoVa informe={ultimo} />
        <LoQueSeBusca keywords={[...actual.deServicio, ...actual.informativas]} datos={google} />
        {/*
         * A todo el ancho y leída como texto (20/10/2026).
         *
         * Era un cuadro de cinco líneas en media pantalla: la de Bilbolink, de
         * 1.490 caracteres, no se podía leer sin bajar dentro del cuadro. Ahora
         * se lee entera, con los enlaces que se pueden pulsar, y se edita con
         * «Editar», en un cuadro que crece con el texto.
         */}
        <div className="estrategia-entera">
          <section className="caja-seo">
            <header>
              <h3>La estrategia</h3>
              {actual.estrategiaDesde && (
                <span className="pill">
                  desde {actual.estrategiaDesde}
                </span>
              )}
              {!editandoEstrategia && (
                <button type="button" className="acc sec" onClick={() => setEditandoEstrategia(true)}>
                  {estrategia.trim() ? "Editar" : "Escribirla"}
                </button>
              )}
            </header>
            {editandoEstrategia ? (
              <textarea
                className="estrategia-texto"
                value={estrategia}
                autoFocus
                onChange={(e) => {
                  setEstrategia(e.target.value);
                  setGuardado(null);
                }}
                placeholder="Qué se va a atacar y por qué. Al cambiarla, la anterior se guarda sola con sus fechas."
                rows={Math.max(12, estrategia.split("\n").length + Math.ceil(estrategia.length / 90) + 2)}
              />
            ) : estrategia.trim() ? (
              <div className="estrategia-leida">{conEnlaces(estrategia)}</div>
            ) : (
              <p className="pista">Este cliente no tiene estrategia escrita. Pulsa «Escribirla».</p>
            )}
            {actual.estrategiasAnteriores.length > 0 && (
              <details className="anteriores">
                <summary>
                  {actual.estrategiasAnteriores.length} anterior(es)
                </summary>
                {actual.estrategiasAnteriores.map((e) => (
                  <p className="pista" key={`${e.desde}-${e.hasta}`}>
                    <span className="pill">
                      {e.desde || "?"} → {e.hasta ?? "hoy"}
                    </span>{" "}
                    {e.texto}
                  </p>
                ))}
              </details>
            )}
          </section>

          <section className="caja-seo">
            <header>
              <h3>Qué toca ahora</h3>
              {ultimo && (
                <span className="pill">
                  del informe de {ultimo.mes}
                </span>
              )}
            </header>
            <PlanVivo informe={ultimo} />
          </section>
        </div>
        <div className="acciones-seo">
          <button
            type="button"
            onClick={() => void guardar().then((ok) => ok && setEditandoEstrategia(false))}
            disabled={ocupado}
          >
            {ocupado ? "Guardando…" : "Guardar"}
          </button>
          {editandoEstrategia && (
            <button
              type="button"
              className="acc sec"
              onClick={() => {
                setEstrategia(ficha.estrategia ?? "");
                setEditandoEstrategia(false);
              }}
            >
              Cancelar
            </button>
          )}
          {guardado && <span className="pista">{guardado}</span>}
        </div>
        {/*
         * El mapa de secciones y el trabajo (26/10/2026): la estrategia se
         * gestiona aquí, no sólo se lee. Mover una keyword de página guarda la
         * estrategia y se ve al momento en «Keywords».
         */}
        <MapaDeSecciones
          clienteId={clienteId}
          mes={seo.datos?.calendario.mes ?? null}
          alGuardar={(f) => {
            const nueva = f as unknown as FichaSeo;
            if (!nueva?.deServicio) return;
            setActual(nueva);
            setKeywords([...nueva.deServicio, ...nueva.informativas]);
          }}
        />
        <IdeasDeKeywords
          clienteId={clienteId}
          alGuardar={(f) => {
            const nueva = f as unknown as FichaSeo;
            if (!nueva?.deServicio) return;
            setActual(nueva);
            setKeywords([...nueva.deServicio, ...nueva.informativas]);
          }}
        />
        <TrabajoDelCliente clienteId={clienteId} />
      </>
    ),
    publicaciones: (
      <>
        {/*
          El calendario de lo que de verdad sale en su web, arriba (24/10/2026):
          Jorge lo pidió «tipo Hootsuite», dentro del cliente. Debajo sigue el
          plan escrito a mano y las ideas.
        */}
        <CalendarioDeContenidos clienteId={clienteId} />
        <Ideas
          clienteId={clienteId}
          yaPrevistos={contenidos}
          alAnadir={(nuevos) => {
            setContenidos([...contenidos, ...nuevos]);
            setGuardado(null);
          }}
        />
        <Contenidos
          contenidos={contenidos}
          keywords={keywords}
          alCambiar={(c) => {
            setContenidos(c);
            setGuardado(null);
          }}
        />
        <div className="acciones-seo">
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={ocupado}
          >
            {ocupado ? "Guardando…" : "Guardar"}
          </button>
          {guardado && <span className="pista">{guardado}</span>}
        </div>
      </>
    ),
    informes: (
      <section className="caja-seo">
        <header>
          <h3>Historia</h3>
          <span className="pista">
            Cada informe queda congelado tal y como se generó: si se
            recalcularan, el mismo mes daría números distintos según
            cuándo lo abras.
          </span>
        </header>

        {/* Generar o rehacer el del mes desde aquí (26/10/2026): antes sólo desde el chat. */}
        <GenerarInforme
          clienteId={clienteId}
          meses={(seo.datos?.calendario.disponibles ?? []).map((m) => ({
            mes: m.mes,
            hecho: informes.some((i) => i.mes === m.mes),
          }))}
          alHacer={recargarInformes}
        />

        {informes.length === 0 ? (
          <p className="pista">
            Todavía no hay informes. Genera el primero con el botón de arriba,
            o pídeselo a BILBOBOT: «hazme el informe del mes de este cliente».
          </p>
        ) : (
          informes.map((inf) => (
            <div className="informe" key={inf.mes}>
              <button
                type="button"
                className="cabecera"
                onClick={() =>
                  setAbierto(abierto === inf.mes ? null : inf.mes)
                }
              >
                <strong>{inf.mes}</strong>
                <span className="pill">
                  generado el {inf.generadoEl}
                </span>
                {inf.comentario && (
                  <span className="pill">con tu lectura</span>
                )}
              </button>
              {abierto === inf.mes && (
                <CuerpoDelInforme
                  clienteId={clienteId}
                  cliente={nombreCliente}
                  informe={inf}
                  onGuardado={recargarInformes}
                />
              )}
            </div>
          ))
        )}
      </section>
    ),
    tareas,
    cliente: quienEs,
    configuracion: comoEstaConectado,
  };

  return (
    <div className="seo-cliente">
      {/**
       * UNA cabecera, TRES pantallas.
       *
       * Aquí había trece pestañas al mismo nivel —seis de métricas encima de
       * siete de gestión— y la fila de gestión quedaba por debajo del resumen
       * entero, fuera de pantalla al entrar. El problema no eran los clics,
       * era el scroll: se abría un cliente y no se veía que hubiera nada más.
       *
       * Ahora la cabecera va arriba con el selector de mes y las tres
       * entradas, y sólo se pinta una pantalla cada vez.
       */}
      <CabeceraSeo
        nombreCliente={nombreCliente}
        calendario={seo.datos?.calendario ?? null}
        onMes={seo.setMes}
        vista={vista}
        onVista={setVista}
      />

      {vista === "como-va" &&
        (seo.cargando ? (
          <p className="pista">Cargando el SEO…</p>
        ) : seo.datos ? (
          <PanelSeo
            clienteId={clienteId}
            datos={seo.datos}
            recargar={seo.recargar}
          />
        ) : (
          <p className="pista">
            No se ha podido cargar el SEO de este cliente. «El cliente» sigue
            funcionando: ahí están la ficha y las conexiones.
          </p>
        ))}

      {/**
       * Informes, con la estrategia encima.
       *
       * La estrategia estaba en un cajón aparte y es el marco de lo que se le
       * cuenta al cliente: se lee antes que los números, no en otra pestaña.
       */}
      {vista === "estrategia" && gestion.estrategia}
      {vista === "informes" && (
        <>
          <div id="informes-del-cliente">{gestion.informes}</div>
        </>
      )}

      {vista === "cliente" && (
        <>
          <nav className="secciones-gestion">
            {DEL_CLIENTE.map(([id, nombre, ayuda]) => (
              <button
                key={id}
                type="button"
                className={apartado === id ? "seccion activa" : "seccion"}
                onClick={() => setApartado(id)}
                title={ayuda}
                aria-current={apartado === id}
              >
                {nombre}
              </button>
            ))}
          </nav>
          {/*
           * Sin conmutador: pulsar el apartado abierto ya no lo cierra.
           *
           * Lo hacía, y dejaba media pantalla en blanco sin decir por qué. Aquí
           * siempre hay uno abierto, que es lo que se espera de una pestaña.
           */}
          {gestion[apartado]}
        </>
      )}
    </div>
  );
}

/**
 * ¿Voy bien con este cliente? Arriba del todo y en una línea.
 *
 * Es la primera pregunta que se hace cualquiera al abrir un cliente, y hasta
 * ahora había que deducirla mirando cuatro tablas. Se saca del último informe
 * —no se recalcula— para que diga lo mismo que se le enseñó al cliente.
 */
function ComoVa({ informe }: { informe: Informe | undefined }) {
  if (!informe) {
    return (
      <div className="como-va vacio">
        Sin informes todavía. Cuando haya uno, aquí sale de un vistazo si el
        cliente va bien.
      </div>
    );
  }

  const c = informe.cuerpo.paraElCliente;
  const s = c?.servicio;

  return (
    <div className="como-va">
      <div className="titular">
        {s && s.cuantas > 0 ? (
          <>
            <strong>
              {s.enPrimeraPagina} de {s.cuantas}
            </strong>{" "}
            keywords de servicio en primera página
            {(s.mejoran > 0 || s.empeoran > 0) && (
              <span className="movimiento">
                <span className="mejor">▲{s.mejoran}</span>{" "}
                <span className="peor">▼{s.empeoran}</span>
              </span>
            )}
          </>
        ) : (
          <span className="pista">
            Sin keywords de servicio guardadas todavía.
          </span>
        )}
        <span className="pill">{informe.mes}</span>
      </div>

      {c?.busqueda && (
        <div className="cifras">
          <Cifra
            que="Clics"
            ahora={c.busqueda.clics}
            antes={c.busqueda.clicsAntes}
          />
          <Cifra
            que="Impresiones"
            ahora={c.busqueda.impresiones}
            antes={c.busqueda.impresionesAntes}
          />
          {/* En posiciones, menos es mejor: de la 30 a la 12 es SUBIR. */}
          <Cifra
            que="Posición media"
            ahora={c.busqueda.posicionMedia}
            antes={c.busqueda.posicionMediaAntes}
            menosEsMejor
          />
          {c.trafico && (
            <Cifra
              que="Sesiones"
              ahora={c.trafico.sesiones}
              antes={c.trafico.sesionesAntes}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * El trabajo del mes, sacado del último informe.
 *
 * No se recalcula aquí: daría una lista distinta de la que se enseñó, y
 * entonces «lo que quedamos en hacer» dependería de cuándo abriste la
 * pantalla.
 */
function PlanVivo({ informe }: { informe: Informe | undefined }) {
  const plan = informe?.cuerpo.planDelMesQueViene ?? [];
  const tecnico = informe?.cuerpo.tecnicoPorArreglar ?? [];

  if (plan.length === 0 && tecnico.length === 0) {
    return (
      <p className="pista">
        Todavía no hay plan. Sale del informe mensual: pídeselo a BILBOBOT con
        «hazme el informe del mes de este cliente».
      </p>
    );
  }

  return (
    <>
      {plan.map((a) => (
        <details className="accion" key={a.queHacer}>
          <summary>
            {a.seNotaEn && (
              <span className={`plazo ${a.seNotaEn}`}>{a.seNotaEn}</span>
            )}
            <strong>{a.queHacer}</strong>
            <span className="pill">{a.clicsEnJuego}</span>
          </summary>
          <p className="pista">{a.porQue}</p>
          {a.como && (
            <p className="como">
              <strong>Cómo:</strong> {a.como}
            </p>
          )}
          {a.cuidado && (
            <p className="cuidado">
              <strong>Cuidado:</strong> {a.cuidado}
            </p>
          )}
        </details>
      ))}

      {tecnico.length > 0 && (
        <details className="tecnico">
          <summary>Lo que falla por dentro ({tecnico.length})</summary>
          {tecnico.map((t) => (
            <div className="accion" key={t.problema}>
              <div className="titulo">
                {t.seNotaEn && (
                  <span className={`plazo ${t.seNotaEn}`}>{t.seNotaEn}</span>
                )}
                <strong>{t.problema}</strong>
                <span className="pill">{t.paginas} págs</span>
              </div>
              <p className="como">{t.como}</p>
            </div>
          ))}
        </details>
      )}
    </>
  );
}

/**
 * Qué hay que escribir, para qué keyword y en qué estado.
 *
 * Es la dirección de contenidos, y faltaba entera: el plan existía en la ficha
 * pero no se veía en ninguna parte ni se podía tocar. Un plan que sólo se puede
 * leer es una lista de buenas intenciones.
 *
 * La keyword se elige de las que ya son objetivo y no se escribe a mano: un
 * artículo que ataca una búsqueda que no está en la estrategia es un artículo
 * que luego no se va a poder medir.
 */
/**
 * Ideas de contenido sacadas de lo que YA se ha medido.
 *
 * No gasta modelo: salen de Search Console y del mapa de enlaces internos, que
 * ya están guardados. Y **no se guardan solas** — se proponen y se eligen. Una
 * lista que se llena sola acaba con cuarenta ideas que nadie decidió, y
 * entonces deja de leerse.
 */
function Ideas({
  clienteId,
  yaPrevistos,
  alAnadir,
}: {
  clienteId: string;
  yaPrevistos: Contenido[];
  alAnadir: (nuevos: Contenido[]) => void;
}) {
  const [ideas, setIdeas] = useState<Contenido[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  const previstos = new Set(
    yaPrevistos.map((c) => c.titulo.trim().toLowerCase()),
  );

  async function buscar(): Promise<void> {
    setBuscando(true);
    try {
      const r = await fetch(
        `/api/memoria/clientes/${clienteId}/ideas-de-contenido`,
        {
          credentials: "same-origin",
        },
      );
      const c = r.ok
        ? ((await r.json()) as { ideas: Contenido[] })
        : { ideas: [] };
      setIdeas(c.ideas ?? []);
    } finally {
      setBuscando(false);
    }
  }

  return (
    <section className="caja-seo">
      <header>
        <h3>Ideas de lo que ya sabemos</h3>
        <span className="pista">
          Salen de Search Console y del mapa de enlaces. No gastan modelo y no
          se guardan hasta que las eliges.
        </span>
      </header>

      <div className="acciones-seo">
        <button type="button" onClick={() => void buscar()} disabled={buscando}>
          {buscando ? "Mirando…" : "Buscar ideas"}
        </button>
      </div>

      {ideas?.length === 0 && (
        <p className="pista">
          Nada que proponer con lo medido hasta ahora. Hace falta un informe
          mensual y el mapa de enlaces; los dos salen solos, pero el primer mes
          no están.
        </p>
      )}

      {(ideas ?? [])
        .filter((i) => !previstos.has(i.titulo.trim().toLowerCase()))
        .map((i) => (
          <div className="idea" key={i.titulo}>
            <div className="titulo">
              <strong>{i.titulo}</strong>
              {i.tipo && <span className="pill">{i.tipo}</span>}
            </div>
            {i.porQue && <p className="pista">{i.porQue}</p>}
            <button
              type="button"
              className="mini"
              onClick={() => alAnadir([i])}
            >
              + añadir al plan
            </button>
          </div>
        ))}
    </section>
  );
}

function Contenidos({
  contenidos,
  keywords,
  alCambiar,
}: {
  contenidos: Contenido[];
  keywords: KeywordObjetivo[];
  alCambiar: (c: Contenido[]) => void;
}) {
  const cambiar = (i: number, campo: Partial<Contenido>) =>
    alCambiar(contenidos.map((c, n) => (n === i ? { ...c, ...campo } : c)));

  // Las que aún no tienen página son las que piden un artículo.
  const sinPagina = keywords.filter((k) => k.texto.trim() && !k.pagina);

  return (
    <section className="caja-seo">
      <header>
        <h3>Contenidos</h3>
        <span className="pista">
          Qué hay que escribir, para qué búsqueda y cómo va.
        </span>
      </header>

      {contenidos.length === 0 && (
        <p className="pista">
          Nada previsto todavía.
          {sinPagina.length > 0 &&
            ` Hay ${sinPagina.length} keyword(s) sin página: ésas son las que piden un artículo.`}
        </p>
      )}

      {contenidos.map((c, i) => (
        <div className="fila-contenido" key={i}>
          <input
            className="titulo"
            value={c.titulo}
            onChange={(e) => cambiar(i, { titulo: e.target.value })}
            placeholder="título del artículo"
          />
          <select
            value={c.keyword ?? ""}
            onChange={(e) =>
              cambiar(i, { keyword: e.target.value || undefined })
            }
          >
            <option value="">— para qué búsqueda —</option>
            {keywords
              .filter((k) => k.texto.trim())
              .map((k) => (
                <option key={k.texto} value={k.texto}>
                  {k.texto}
                </option>
              ))}
          </select>
          <input
            className="mes"
            value={c.mes ?? ""}
            onChange={(e) => cambiar(i, { mes: e.target.value || undefined })}
            placeholder="mes"
          />
          <select
            value={c.estado ?? "previsto"}
            onChange={(e) =>
              cambiar(i, {
                estado: e.target.value as "previsto" | "escrito" | "publicado",
              })
            }
          >
            <option value="previsto">previsto</option>
            <option value="escrito">escrito</option>
            <option value="publicado">publicado</option>
          </select>
          <button
            type="button"
            className="quitar"
            onClick={() => alCambiar(contenidos.filter((_, n) => n !== i))}
          >
            ✕
          </button>
          <textarea
            className="encargo"
            value={c.notas ?? ""}
            onChange={(e) => cambiar(i, { notas: e.target.value || undefined })}
            placeholder="El encargo para quien lo escriba: enfoque, qué cubrir, a qué página enlazar. Pídeselo a BILBOBOT y pégalo aquí."
            rows={2}
          />
        </div>
      ))}

      <button
        type="button"
        className="anadir"
        onClick={() =>
          alCambiar([...contenidos, { titulo: "", estado: "previsto" }])
        }
      >
        + añadir contenido
      </button>
    </section>
  );
}

/**
 * Un informe congelado, leído.
 *
 * Va en el orden en que se cuenta: primero lo que se le enseña al cliente,
 * luego lo de dentro, y al final lo que se propuso hacer entonces.
 */
function CuerpoDelInforme({
  clienteId,
  cliente,
  informe,
  onGuardado,
}: {
  clienteId: string;
  cliente: string;
  informe: Informe;
  /** Para que la lista de arriba se entere de lo que se acaba de escribir. */
  onGuardado: () => void;
}) {
  const [comentario, setComentario] = useState(informe.comentario ?? "");
  /*
   * Los tres bloques que se pueden escribir, y NADA más.
   *
   * No hay ningún campo aquí para las visitas ni para los contactos, y es a
   * propósito: un informe que se puede editar entero es un informe que puede
   * acabar diciendo algo que el dato no dice. Se editan las palabras; los
   * números salen de lo que se midió y se quedan como estaban.
   */
  const [lectura, setLectura] = useState(informe.redaccion?.lectura ?? "");
  const [estrategia, setEstrategia] = useState(
    informe.redaccion?.estrategia ?? "",
  );
  const [plan, setPlan] = useState(informe.redaccion?.plan ?? "");
  const [estado, setEstado] = useState<string | null>(null);
  const [verDocumento, setVerDocumento] = useState(false);
  const c = informe.cuerpo.paraElCliente;
  const interno = informe.cuerpo.interno;

  async function guardarComentario(): Promise<void> {
    const r = await fetch(
      `/api/memoria/clientes/${clienteId}/informes/${informe.mes}`,
      {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ comentario, lectura, estrategia, plan }),
      },
    );
    setEstado(r.ok ? "Guardado." : "No se ha podido guardar.");
    if (r.ok) onGuardado();
  }

  return (
    <div className="cuerpo">
      {/**
       * LOS NÚMEROS DE ESE MES, que es a lo que se abre un informe.
       *
       * Estaban guardados y no se pintaban: desplegar marzo enseñaba el texto
       * y ni una sola cifra de marzo. Sólo los leía el resumen de arriba, y
       * sólo del último informe — así que el histórico existía y no había forma
       * de verlo salvo abriendo el documento imprimible.
       *
       * Van congelados a propósito. Si se recalcularan, el mismo mes daría
       * números distintos según cuándo lo abrieras, y dejaría de servir para
       * comprobar lo que se le enseñó al cliente.
       */}
      {(c?.trafico || c?.busqueda || c?.servicio) && (
        <section className="numeros-del-mes">
          <header>
            <strong>Los números de {mesLegible(informe.mes)}</strong>
            <span className="pista">
              Tal y como se congelaron al generar el informe.
            </span>
          </header>

          {/*
           * Los informes viejos NO miden el mes que dicen medir.
           *
           * Hasta el 25 de agosto de 2026, a Google se le pedían «los últimos
           * 28 días desde hoy» en vez del mes natural. Así que el informe de
           * agosto generado el 18 mide del 21 de julio al 18 de agosto, y dice
           * 4.194 visitas cuando agosto entero lleva 154 y julio tuvo 8.900.
           *
           * No se recalculan —un informe congelado tiene que seguir diciendo lo
           * que se le enseñó al cliente—, así que se marcan. Un número falso sin
           * su aviso al lado se vuelve a creer cada vez que alguien lo abre.
           */}
          {informe.generadoEl < FIN_DE_LOS_28_DIAS && (
            <p className="aviso-ventana">
              Este informe se generó pidiendo <b>los últimos 28 días</b>, no el
              mes natural: sus cifras cubren parte del mes anterior. Desde el 25
              de agosto de 2026 se pide el mes exacto.
            </p>
          )}

          <div className="cifras">
            {c.trafico && (
              <Cifra
                que="Visitas"
                ahora={c.trafico.sesiones}
                antes={c.trafico.sesionesAntes}
              />
            )}
            {c.busqueda && (
              <>
                <Cifra
                  que="Clics desde Google"
                  ahora={c.busqueda.clics}
                  antes={c.busqueda.clicsAntes}
                />
                <Cifra
                  que="Impresiones"
                  ahora={c.busqueda.impresiones}
                  antes={c.busqueda.impresionesAntes}
                />
                {/* Menos es mejor: de la 30 a la 12 son dieciocho puestos
                    ganados, y la flecha tiene que decir eso y no lo contrario. */}
                <Cifra
                  que="Posición media"
                  ahora={c.busqueda.posicionMedia}
                  antes={c.busqueda.posicionMediaAntes}
                  menosEsMejor
                />
              </>
            )}
          </div>

          {c.servicio && c.servicio.cuantas > 0 && (
            <p className="pista">
              De {enPlural(c.servicio.cuantas, "búsqueda", "búsquedas")} que
              trabajamos, <b>{c.servicio.enPrimeraPagina}</b>{" "}
              {c.servicio.enPrimeraPagina === 1 ? "estaba" : "estaban"} en la
              primera página. Subieron <b>{c.servicio.mejoran}</b> y bajaron{" "}
              <b>{c.servicio.empeoran}</b>.
            </p>
          )}
          {/*
           * Las de blog, que se miden desde siempre y no se escribían.
           *
           * Es el trabajo de contenidos: sin esta línea, un mes entero de
           * artículos no deja rastro en el informe.
           */}
          {c.informativas && c.informativas.cuantas > 0 && (
            <p className="pista">
              Y de {enPlural(c.informativas.cuantas, "búsqueda", "búsquedas")}{" "}
              del blog, <b>{c.informativas.enPrimeraPagina}</b> en primera
              página.
            </p>
          )}

          <p className="pista fuentes-informe">
            Visitas, de Analytics · clics, impresiones y posición media, de
            Search Console · las búsquedas trabajadas, de SE Ranking.
          </p>
        </section>
      )}

      {Array.isArray(c?.enLasIas) && c.enLasIas.length > 0 && (
        <div className="en-ias">
          <div className="s">
            <strong>Cuando le preguntan a una IA</strong>
            <span className="pista">
              No es literalmente ChatGPT: es el mismo modelo por API y con
              búsqueda web.
            </span>
          </div>
          {c.enLasIas.map((v) => (
            <div className="accion" key={v.keyword}>
              <div className="titulo">
                <strong>{v.keyword}</strong>
                <span className={v.teNombran ? "pill mejor" : "pill peor"}>
                  {v.teNombran
                    ? `${v.puesto}º de ${v.deCuantos}`
                    : `no sale, de ${v.deCuantos}`}
                </span>
              </div>
              {v.salenEnTuLugar.length > 0 && (
                <div className="competencia">
                  {v.salenEnTuLugar.map((r) => (
                    <p className="pista" key={r.nombre}>
                      <strong>{r.nombre}</strong>
                      {r.porQue ? ` — ${r.porQue}` : ""}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lo que sólo salía en el documento (26/10/2026): la demanda de Google Ads y lo publicado. */}
      {c?.demanda && c.demanda.total > 0 && (
        <div className="s">
          <strong>Cuánta gente lo busca en {c.demanda.zona}:</strong>{" "}
          {c.demanda.total.toLocaleString("es-ES")} búsquedas al mes ·{" "}
          {c.demanda.keywords
            .filter((k) => k.busquedas > 0)
            .slice(0, 6)
            .map((k) => `${k.keyword} (${k.busquedas.toLocaleString("es-ES")}${k.posicion ? `, estás en la ${k.posicion}` : ""})`)
            .join(" · ")}
        </div>
      )}

      {(c?.contenidosPublicados?.length ?? 0) > 0 && (
        <div className="s">
          <strong>Publicado:</strong> {c!.contenidosPublicados!.map((p) => p.titulo).join(" · ")}
        </div>
      )}

      {(c?.seHizo?.length ?? 0) > 0 && (
        <div className="s">
          <strong>Se hizo:</strong> {c!.seHizo!.join(" · ")}
        </div>
      )}

      {(c?.siFunciono?.length ?? 0) > 0 && (
        <div className="s">
          <strong>Si funcionó</strong>{" "}
          <span className="pista">(medido 30 días después de hacerlo)</span>
          <ul>
            {c!.siFunciono!.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        </div>
      )}

      {(c?.problemasArreglados?.length ?? 0) > 0 && (
        <div className="s">
          <strong>Arreglado:</strong>{" "}
          {c!.problemasArreglados!.map((x) => (
            <span className="pill" key={x}>
              {x}
            </span>
          ))}
        </div>
      )}

      {interno && (
        <details className="interno">
          <summary>Interno — no se le enseña al cliente</summary>
          {interno.planDelMes && (
            <p className="pista">
              {interno.planDelMes.hechas} hechas de{" "}
              {interno.planDelMes.propuestas} propuestas
              {interno.planDelMes.descartadas > 0 &&
                `, ${interno.planDelMes.descartadas} descartadas`}
              .
            </p>
          )}
          {(interno.planDelMes?.sinHacer.length ?? 0) > 0 && (
            <ul className="pista">
              {interno.planDelMes!.sinHacer.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {(interno.problemasNuevos?.length ?? 0) > 0 && (
            <p className="pista">
              <strong>Nuevos:</strong> {interno.problemasNuevos!.join(" · ")}
            </p>
          )}
        </details>
      )}

      {(informe.cuerpo.planDelMesQueViene?.length ?? 0) > 0 && (
        <details className="interno">
          {/**
           * Dentro de un informe esto es PASADO: es lo que se propuso
           * entonces. El plan vivo está arriba, en «Qué toca ahora».
           */}
          <summary>
            Lo que se propuso hacer ({informe.cuerpo.planDelMesQueViene!.length}
            )
          </summary>
          {informe.cuerpo.planDelMesQueViene!.map((a) => (
            <p className="pista" key={a.queHacer}>
              {a.queHacer}
            </p>
          ))}
        </details>
      )}

      {/* El informe no se toca; esto sí, porque son palabras y no datos. */}
      <div className="s">
        <strong>Tu lectura del mes</strong>
        <span className="pista">
          Sale la primera y en cursiva, debajo del título. Es lo único del
          informe que no viene de un dato.
        </span>
      </div>
      <textarea
        className="estrategia-texto"
        value={lectura}
        onChange={(e) => {
          setLectura(e.target.value);
          setEstado(null);
        }}
        rows={3}
        placeholder="Cómo ves tú este mes. Va en el informe que ve el cliente."
      />

      <div className="s">
        <strong>Notas internas</strong>
        <span className="pista">
          Esto NO sale en el informe del cliente. Es para acordarte tú.
        </span>
      </div>
      <textarea
        className="estrategia-texto"
        value={comentario}
        onChange={(e) => {
          setComentario(e.target.value);
          setEstado(null);
        }}
        rows={2}
        placeholder="Lo que quieras recordar y no le interesa al cliente."
      />
      <div className="s">
        <strong>Hacia dónde vamos</strong>
        <span className="pista">
          La estrategia, contada al cliente. Sale en el informe justo antes del
          plan del mes que viene.
        </span>
      </div>
      <textarea
        className="estrategia-texto"
        value={estrategia}
        onChange={(e) => {
          setEstrategia(e.target.value);
          setEstado(null);
        }}
        rows={3}
        placeholder="Adónde vamos con este cliente y por qué. Si lo dejas vacío, no sale la sección."
      />

      <div className="s">
        <strong>El plan del mes que viene, con tus palabras</strong>
        <span className="pista">
          Si escribes aquí, SUSTITUYE a la lista que genera el sistema — no se
          suma. Dos planes distintos en el mismo informe es peor que cualquiera
          de los dos.
        </span>
      </div>
      <textarea
        className="estrategia-texto"
        value={plan}
        onChange={(e) => {
          setPlan(e.target.value);
          setEstado(null);
        }}
        rows={3}
        placeholder="Déjalo vacío para usar el plan que propone el sistema."
      />

      <div className="acciones-seo">
        <button type="button" onClick={() => void guardarComentario()}>
          Guardar lo escrito
        </button>
        <button type="button" onClick={() => setVerDocumento(true)}>
          Ver como documento
        </button>
        {estado && <span className="pista">{estado}</span>}
      </div>

      {verDocumento && (
        <InformeImprimible
          cliente={cliente}
          mes={informe.mes}
          cuerpo={informe.cuerpo as unknown as Record<string, unknown>}
          redaccion={{ lectura, estrategia, plan }}
          cerrar={() => setVerDocumento(false)}
        />
      )}
    </div>
  );
}
